import "../styles/splashscreen.css";
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/images/logo.png";

// Unique CSS class prefix to prevent conflicts
const CSS_PREFIX = "splash-ultra-";

const preloadImages = (imagePaths) => {
  imagePaths.forEach(src => {
    const img = new Image();
    img.src = src;
  });
};

const SplashScreen = () => {
    const [isClosing, setIsClosing] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const navigate = useNavigate();

    const imagesData = useMemo(() => [
        { src: "_01.png", heading: "Mir Sarfaraz Bugti", description: "Chief Minister Balochistan" },
        { src: "_02.png", heading: "Capt (R) Abdul Khaliq Khan Achakzai", description: "Speaker Balochistan Assembly" },
        { src: "_03.png", heading: "Shakeel Qadar", description: "Chief Secratary Balochistan" },
        { src: "_04.png", heading: "Zahid Saleem", description: "ACS Home Department" },
        { src: "_05.png", heading: "Hamza Shafqaat", description: "Comissioner Quetta Division" },
        { src: "_06.png", heading: "Habib Ahmed Bangulzai", description: "Deputy Commissioner Chaman" },
    ], []);

    // Preload images on component mount
    useEffect(() => {
        const imagePaths = imagesData.map(img => require(`../assets/images/${img.src}`));
        preloadImages(imagePaths);
    }, [imagesData]);

    // Animation timer and navigation
    useEffect(() => {
        const totalDuration = 12000; // 12 seconds
        const intervalDuration = 2000; // Rotate every 2 seconds
        const progressInterval = 50; // Update progress every 50ms
        
        // Progress animation
        const progressTimer = setInterval(() => {
            setProgress(prev => {
                const newProgress = prev + (progressInterval / totalDuration * 100);
                return newProgress > 100 ? 100 : newProgress;
            });
        }, progressInterval);

        // Image rotation
        const rotationInterval = setInterval(() => {
            setActiveIndex(prev => (prev + 1) % imagesData.length);
        }, intervalDuration);

        // Close splash screen after total duration
        const closeTimer = setTimeout(() => {
            setIsClosing(true);
            clearInterval(rotationInterval);
            clearInterval(progressTimer);
            setTimeout(() => navigate("/login"), 800); // Smooth transition
        }, totalDuration);

        return () => {
            clearInterval(rotationInterval);
            clearInterval(progressTimer);
            clearTimeout(closeTimer);
        };
    }, [navigate, imagesData.length]);

    return (
        <div className={`${CSS_PREFIX}container ${isClosing ? `${CSS_PREFIX}closing` : ""}`}>
            {/* Particle background */}
            <div className={`${CSS_PREFIX}particles`}>
                {Array.from({ length: 30 }).map((_, i) => (
                    <div key={i} className={`${CSS_PREFIX}particle`} style={{
                        '--size': `${Math.random() * 0.5 + 0.2}vmin`,
                        '--x': `${Math.random() * 100}%`,
                        '--y': `${Math.random() * 100}%`,
                        '--delay': `${Math.random() * 2}s`,
                        '--duration': `${Math.random() * 10 + 10}s`
                    }}></div>
                ))}
            </div>
            
            {/* Glowing orb */}
            <div className={`${CSS_PREFIX}orb`}></div>

            {/* Main content */}
            <div className={`${CSS_PREFIX}content-wrapper`}>
                {/* Logo with floating animation */}
                <div className={`${CSS_PREFIX}logo-container`}>
                    <img 
                        src={logo} 
                        alt="Logo" 
                        className={`${CSS_PREFIX}logo`} 
                        loading="eager"
                    />
                    <h1 className={`${CSS_PREFIX}title`}>
                        <span className={`${CSS_PREFIX}title-part`}>Welcome to</span>
                        <span className={`${CSS_PREFIX}title-part ${CSS_PREFIX}highlight`}>E-Administration</span>
                        <span className={`${CSS_PREFIX}title-part`}>Chaman</span>
                    </h1>
                    <p className={`${CSS_PREFIX}subtitle`}>Transforming Government Services</p>
                </div>

                {/* 3D Image carousel */}
                <div className={`${CSS_PREFIX}carousel-container`}>
                    <div className={`${CSS_PREFIX}carousel-track`}>
                        {imagesData.map((imageData, index) => (
                            <div 
                                className={`${CSS_PREFIX}carousel-slide ${index === activeIndex ? `${CSS_PREFIX}active` : ''}`}
                                key={index}
                                style={{ '--offset': (index - activeIndex) }}
                            >
                                <div className={`${CSS_PREFIX}profile-card`}>
                                    <div className={`${CSS_PREFIX}image-wrapper`}>
                                        <img
                                            src={require(`../assets/images/${imageData.src}`)}
                                            alt={imageData.heading}
                                            className={`${CSS_PREFIX}profile-image`}
                                            loading={index === 0 ? "eager" : "lazy"}
                                        />
                                        <div className={`${CSS_PREFIX}image-glow`}></div>
                                    </div>
                                    <div className={`${CSS_PREFIX}profile-info`}>
                                        <h2 className={`${CSS_PREFIX}profile-heading`}>{imageData.heading}</h2>
                                        <p className={`${CSS_PREFIX}profile-description`}>{imageData.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Animated progress bar */}
            <div className={`${CSS_PREFIX}progress-container`}>
                <div 
                    className={`${CSS_PREFIX}progress-bar`} 
                    style={{ width: `${progress}%` }}
                ></div>
                <div className={`${CSS_PREFIX}progress-text`}>
                    Loading... {Math.min(100, Math.round(progress))}%
                </div>
            </div>

            {/* Floating decorative elements */}
            <div className={`${CSS_PREFIX}floating-shapes`}>
                <div className={`${CSS_PREFIX}shape ${CSS_PREFIX}shape-1`}></div>
                <div className={`${CSS_PREFIX}shape ${CSS_PREFIX}shape-2`}></div>
                <div className={`${CSS_PREFIX}shape ${CSS_PREFIX}shape-3`}></div>
            </div>
        </div>
    );
};

export default SplashScreen;